import { ComponentType } from '@angular/cdk/portal';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LngLatLike } from 'mapbox-gl';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, pairwise, take } from 'rxjs/operators';
import { BuildingQuery } from 'src/app/geometrics-form/buildings/state/building.query';
import { BuildingService } from 'src/app/geometrics-form/buildings/state/building.service';
import { VertexQuery } from 'src/app/geometrics-form/vertexes/state/vertex.query';
import { VertexService } from 'src/app/geometrics-form/vertexes/state/vertex.service';
import { MapService } from 'src/app/shared/components/map/map.service';
import { WindowService } from 'src/app/shared/components/window/window.service';
import { SimulationFormComponent } from 'src/app/simulation/simulation-form/simulation-form.component';
import { ProjectUI } from '../state/project-ui';
import { ProjectQuery } from '../state/project.query';
import { ProjectService } from '../state/project.service';

@Component({
  selector: 'app-project',
  templateUrl: './project.component.html',
  styleUrls: ['./project.component.scss']
})
export class ProjectComponent implements OnInit, OnDestroy {

  projectUi$ = this.projectQuery.selectUi(this.projectQuery.getActiveId());

  vertexesInvalid: number[] = [];

  currentVertexId: number;
  currentBuildingId: number;
  subs: Subscription[] = [];

  constructor(
    private projectQuery: ProjectQuery,
    private projectService: ProjectService,
    private windowService: WindowService,
    private vertexService: VertexService,
    private vertexQuery: VertexQuery,
    private buildingService: BuildingService,
    private buildingQuery: BuildingQuery,
    private mapService: MapService,
    private snakBar: MatSnackBar
  ) { }

  ngOnInit(): void {

    const subProjectUi = this.projectUi$
      .pipe(
        debounceTime(3000),
        distinctUntilChanged((x, y) => JSON.stringify(x) === JSON.stringify(y))
      )
      .subscribe(projectUi => this.validateProject(projectUi));

    this.subs.push(subProjectUi);

    const subVertex = this.vertexQuery.invalid$
      .pipe(
        debounceTime(1000)
      ).subscribe(vertexes => {
        this.vertexesInvalid = vertexes.map(v => v.id);
      });

    this.subs.push(subVertex);

    const subProjectQuery = this.projectQuery.selectActive().pipe(
      map(p => p.validated),
      distinctUntilChanged(),
      pairwise()
    ).subscribe(pair => {

      if (!pair[0] && pair[1]) {
        this.snakBar.open($localize`Project validated`, $localize`NETWORK`, { duration: 2000 })
          .onAction().subscribe(() => this.newNetwork());
      }
    });

    this.subs.push(subProjectQuery);
  }

  ngOnDestroy(): void {
    this.subs.forEach(sub => sub.unsubscribe());
    this.windowService.closeAll();
  }

  flyToBuilding(buildingIds: number[]) {
    this.currentBuildingId = buildingIds.find(id => id !== this.currentBuildingId);
    const building = this.buildingQuery.getEntity(this.currentBuildingId);
    this.flyTo([building.longitude, building.latitude]);
    this.buildingService.updateUI({ visible: true });
    this.mapService.map$.subscribe(m => m.setLayoutProperty('mitra-building-conn', 'visibility', 'visible'));
  }

  flyToVertex(vertexIds: number[]) {
    this.currentVertexId = vertexIds.find(id => id !== this.currentVertexId);
    const vertex = this.vertexQuery.getEntity(this.currentVertexId);
    this.flyTo([vertex.longitude, vertex.latitude]);
    this.vertexService.updateUI({ visible: true });
  }

  private validateProject(projectUi: ProjectUI) {
    if (projectUi && this.projectService.isValid(projectUi)) {
      this.projectService.validate(this.projectQuery.getActiveId())
        .pipe(take(1)).subscribe();
    }
  }

  private open(title: string, componentType: ComponentType<any>) {
    this.windowService.open({ title, componentType });
  }

  private flyTo(center: LngLatLike) {
    this.mapService.map$.subscribe(m => m.flyTo({ center }));
  }

  private newNetwork() {
    const titleConfig = $localize`NEW NETWORK`;
    this.windowService.open({
      title: titleConfig,
      componentType: SimulationFormComponent
    });
    this.windowService.close(this);
  }

}
